import { condition } from '@ofmos/shared/filter-ast';
import { useDeferredValue, useMemo, useState } from 'react';
import { useFields, useLinkJunction, useObject, useRecordList } from '../../../data/hooks';
import { Button, Dialog, SearchField, Skeleton, useToast } from '../../../ui';
import { recordTitle } from '../../records/table/tableModel';
import { PickerOption } from './PickerOption';

/**
 * THE SEARCHING PICKER (D5).
 *
 * It is NOT `RelationInput`'s menu lifted into a dialog, and the reason is measured
 * rather than aesthetic: `SelectMenu` has nine props and none of them is search, and
 * `RelationInput` fetches a flat `PICKER_LIMIT = 50` with no filter and the server's
 * default order - its own docblock marks that ceiling and calls a searching picker a
 * later job. On a 300-person object that lift ships a picker that cannot find 250 of
 * them.
 *
 * So this one FILTERS: `contains(<label field>, term)` with a small limit, through the
 * shared AST (INV-6) and the same `POST /api/query` everything else uses. TEXT
 * supports `contains`, the label field is TEXT by definition, and no server work is
 * needed. `RelationInput` itself is deliberately untouched - its ceiling is a separate,
 * still-open item.
 *
 * The term is DEFERRED rather than debounced with a timer: React already knows how to
 * keep the input responsive while a slower render catches up, and a timer would be a
 * second thing to tune and to clean up on unmount.
 */
const PICKER_RESULT_LIMIT = 20;

export interface LinkPickerProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly parentObjectId: string;
  readonly parentRecordId: string;
  /** The MANY_TO_MANY field the membership belongs to. */
  readonly relationFieldId: string;
  /** The object being searched: the other side of the membership. */
  readonly targetObjectId: string;
  /** True when THIS record is the junction's source; it decides the write's shape. */
  readonly parentIsSource: boolean;
  readonly sectionTitle: string;
}

export function LinkPicker({
  open,
  onOpenChange,
  parentObjectId,
  parentRecordId,
  relationFieldId,
  targetObjectId,
  parentIsSource,
  sectionTitle,
}: LinkPickerProps) {
  const toast = useToast();
  const [term, setTerm] = useState('');
  const deferredTerm = useDeferredValue(term);

  const target = useObject(targetObjectId);
  const targetFields = useFields(targetObjectId);
  const link = useLinkJunction(parentObjectId);

  const labelFieldId = target.data?.labelIdentifierFieldId ?? '';
  const trimmed = deferredTerm.trim();

  /**
   * An empty term lists the first page rather than nothing: opening a picker on an
   * object with four records and being asked to type is the worse of the two failures.
   */
  const filter = useMemo(
    () =>
      trimmed === '' || labelFieldId === ''
        ? undefined
        : condition(labelFieldId, 'contains', trimmed),
    [trimmed, labelFieldId],
  );

  const select = useMemo(
    () =>
      (targetFields.data ?? [])
        .filter((field) => field.id === labelFieldId)
        .map((field) => field.id),
    [targetFields.data, labelFieldId],
  );

  /**
   * AN OBJECT WITH NO RECORD TITLE CANNOT BE SEARCHED, and that has to be a sentence
   * rather than a spinner. `resolveLabelField` is TEXT-only in all three of its steps,
   * so an object with no TEXT field has no label id - and then there is nothing to
   * search on, nothing to sort by, and nothing to show in a row.
   */
  const searchable = labelFieldId !== '' && select.length > 0;
  const results = useRecordList(targetObjectId, {
    select,
    filter,
    ...(searchable ? { orderBy: [{ fieldId: labelFieldId, direction: 'asc' }] } : {}),
    limit: PICKER_RESULT_LIMIT,
    enabled: open && searchable,
  });

  const add = (recordId: string, label: string) => {
    link.mutate(
      {
        relationFieldId,
        sourceId: parentIsSource ? parentRecordId : recordId,
        targetId: parentIsSource ? recordId : parentRecordId,
      },
      {
        onSuccess: (result) => {
          onOpenChange(false);
          setTerm('');
          /**
           * D5: "is a member again" is a different fact from "was added", and only the
           * response knows which happened - a re-link RESTORES the previous membership
           * with its role history rather than creating a second row.
           */
          toast({
            title: result.restored ? `${label} is a member again` : `${label} was added`,
            tone: 'success',
          });
        },
        onError: (error) =>
          toast({
            title: `Could not add ${label}`,
            description: error.message,
            tone: 'danger',
          }),
      },
    );
  };

  const ids = results.data?.ids ?? [];

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Add to ${sectionTitle}`}
      description={`Search ${target.data?.labelPlural.toLowerCase() ?? 'records'} and pick one to add. Nothing else about them changes.`}
      width="sm"
      footer={
        <Button variant="secondary" onClick={() => onOpenChange(false)}>
          Done
        </Button>
      }
    >
      <div className="space-y-3">
        <SearchField
          context="content"
          label={`Search ${target.data?.labelPlural.toLowerCase() ?? 'records'}`}
          placeholder={`Search ${target.data?.labelPlural.toLowerCase() ?? 'records'}`}
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          autoFocus
        />

        {!searchable && !targetFields.isPending ? (
          <p className="py-4 text-sm text-ink-muted">
            {`${target.data?.labelPlural ?? 'These records'} have no record title yet, so there is nothing to search on. An admin can set one under Configure.`}
          </p>
        ) : results.isPending ? (
          <div className="space-y-2" aria-busy="true">
            <Skeleton className="h-9" label="Searching" />
            <Skeleton className="h-9" />
          </div>
        ) : results.isError ? (
          <p role="alert" className="text-sm text-danger">
            {`The search could not be run. ${results.error.message}`}
          </p>
        ) : ids.length === 0 ? (
          <p className="py-4 text-sm text-ink-muted">
            {trimmed === ''
              ? `There are no ${target.data?.labelPlural.toLowerCase() ?? 'records'} to add yet.`
              : `Nothing matches "${trimmed}". Try a shorter search.`}
          </p>
        ) : (
          <ul className="divide-y divide-line-subtle">
            {ids.map((id) => (
              <PickerOption
                key={id}
                objectId={targetObjectId}
                recordId={id}
                labelFieldId={labelFieldId}
                disabled={link.isPending}
                onPick={(row) => add(id, recordTitle(row, labelFieldId))}
              />
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
}
